import styled from "@emotion/styled";
import { fontAngkor, fontFredoka } from "@product/scmp-sdk";

import { BaseLink } from "~/components/common/base-link";
import { ContentCoverImage } from "~/components/content/content-cover-image";
import { Figure } from "~/components/content/content-cover-image/styles";
import {
  Headline as ContentHeadline,
  SponsorHeadline,
} from "~/components/content/content-headline/styles";
import { EntityLink } from "~/components/entity-link";
import { Button } from "~/components/posties/button";
import { OffsetContainer } from "~/components/posties/offset-container";

import BlueBook from "./blue-book.svg?url";
import Flag from "./flag.svg?url";
import LightBlueBook from "./light-blue-book.svg?url";
import MobileFlag from "./mobile-flag.svg?url";

export const StyledButton = styled(Button)``;

export const StyledContentCoverImage = styled(ContentCoverImage)`
  border-radius: 20px;

  overflow: hidden;
`;

export const FlagContainer = styled.div`
  position: absolute;
  inset-block-start: -10px;
  inset-inline-end: 17px;
  z-index: 1;

  ${props => props.theme.breakpoints.up("tablet")} {
    inset-block-start: -15px;
    inset-inline-end: 22.89px;
  }
  ${props => props.theme.breakpoints.up("desktop")} {
    inset-block-start: -18px;
    inset-inline-end: 67.72px;
  }
`;

export const StyledFlag = styled.div`
  inline-size: 65.3px;
  block-size: 64.2px;

  background-image: url(${MobileFlag});
  background-repeat: no-repeat;
  background-size: contain;

  ${props => props.theme.breakpoints.up("tablet")} {
    inline-size: 98.66px;
    block-size: 97px;

    background-image: url(${Flag});
  }
  ${props => props.theme.breakpoints.up("desktop")} {
    inline-size: 119px;
    block-size: 117px;
  }
`;

export const FlagText = styled.div`
  position: absolute;
  inset-block-start: 42%;
  inset-inline-start: 57%;

  inline-size: 81%;

  color: #ffffff;
  font-size: 12px;
  font-family: ${fontAngkor};
  letter-spacing: -0.02em;
  text-align: center;
  text-transform: uppercase;

  transform: translate(-50%, -50%);

  ${props => props.theme.breakpoints.up("tablet")} {
    font-size: 20px;
  }
  ${props => props.theme.breakpoints.up("desktop")} {
    font-size: 24px;
  }

  span {
    display: block;

    font-size: 24px;
    line-height: 22px;
    ${props => props.theme.breakpoints.up("tablet")} {
      font-size: 40px;
      line-height: 33px;
    }
    ${props => props.theme.breakpoints.up("desktop")} {
      font-size: 48px;
      line-height: 40px;
    }
  }
`;

export const StyledOffsetContainer = styled(OffsetContainer)`
  margin-inline-start: 10px;
  aspect-ratio: 3/2;
  ${props => props.theme.breakpoints.up("largeDesktop")} {
    aspect-ratio: 16/9;
  }

  ${Figure} {
    position: relative;
    inset-block-start: 8px;
    inset-inline-end: 8px;

    inline-size: 100%;
    block-size: 100%;

    border-radius: 20px;

    ${props => props.theme.breakpoints.up("tablet")} {
      inset-block-start: 9px;
      inset-inline-end: 9px;
    }
    ${props => props.theme.breakpoints.up("desktop")} {
      inset-block-start: 10px;
      inset-inline-end: 10px;
    }
  }
`;

export const Headline = styled.div`
  margin-block-end: 16px;

  color: #1b4191;
  ${ContentHeadline}, ${SponsorHeadline} {
    inline-size: 100%;

    font-weight: 400;
    font-size: 24px;
    font-family: ${fontAngkor};
    line-height: 28px;
    letter-spacing: -0.72px;
    text-align: center;
    ${props => props.theme.breakpoints.up("tablet")} {
      margin-block: 41px 20px;

      font-size: 32px;
      line-height: 38px;
      letter-spacing: -0.96px;
    }
    ${props => props.theme.breakpoints.up("desktop")} {
      margin-block-start: 42px;
    }
  }
`;

export const StyledEntityLink = styled(EntityLink)``;

export const Content = styled.div`
  position: relative;
  z-index: 1;

  margin-block-start: 24px;
  margin-inline: auto;
  padding-inline: 24px;

  text-align: center;
  ${props => props.theme.breakpoints.up("tablet")} {
    max-inline-size: 640px;
    margin-block-start: 40px;
    padding-inline: 0;
  }
  ${props => props.theme.breakpoints.up("desktop")} {
    max-inline-size: 822px;
  }
`;

export const Container = styled.div``;

export const Section = styled(BaseLink)`
  inline-size: fit-content;
  margin-block-end: 16px;
  margin-inline: auto;
  padding-block: 4.13px;
  padding-inline: 8.26px;

  color: #ffffff;
  font-weight: 600;
  font-size: 9.3px;
  font-family: ${fontFredoka};
  line-height: 12.4px;
  letter-spacing: 0.05em;
  text-align: center;
  text-transform: uppercase;

  border-radius: 4.13px;

  background: #ff7c1d;

  ${props => props.theme.breakpoints.up("tablet")} {
    margin-block-end: 12px;
    padding-block: 8px;
    padding-inline: 16px;

    font-size: 18px;
    line-height: 24px;

    border-radius: 8px;
  }
`;

export const StyledImageEntityLink = styled(EntityLink)`
  position: relative;
  z-index: 1;

  display: grid;
  grid:
    "image" min-content
    / 100%;
  justify-content: center;
  align-items: center;

  inline-size: 100%;
  margin-inline: auto;
  padding-inline: 24px;
  ${props => props.theme.breakpoints.up("tablet")} {
    inline-size: 641.09px;
    padding-inline: 0;
  }
  ${props => props.theme.breakpoints.up("desktop")} {
    inline-size: 845.22px;
    margin-inline: auto;
  }
  ${props => props.theme.breakpoints.up("largeDesktop")} {
    inline-size: 942px;
    aspect-ratio: 16/9;

    &::before {
      position: absolute;
      inset-block-start: -92px;
      inset-inline-start: -219px;
      content: "";

      inline-size: 365px;
      block-size: 305px;

      background: url(${LightBlueBook});
    }

    &::after {
      position: absolute;
      inset-block-end: -136px;
      inset-inline-end: -197px;
      content: "";

      inline-size: 314.5px;
      block-size: 273.61px;

      background: url(${BlueBook});
    }
  }

  ${StyledOffsetContainer} {
    grid-area: image;
  }
`;

export const ImageWrapper = styled.div`
  position: relative;

  display: block;
`;
